import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase/client';
import { AuthContextType } from './AuthContext.types';
import { useUserProfileStore } from '@/lib/store/userProfileStore';

// Create a broadcast channel for cross-tab communication
const authChannel = new BroadcastChannel('auth_channel');

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionChecked, setSessionChecked] = useState<boolean>(false);

  // Create user profile if it doesn't exist
  const createUserProfileIfNeeded = async (currentUser: SupabaseUser) => {
    try {
      if (!currentUser?.id || !supabase) return;
      
      // First check if the user profile already exists
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('id')
        .eq('id', currentUser.id)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        console.error('Error checking for existing user profile:', fetchError);
        return;
      }
      
      // If user doesn't exist, create profile in the database
      if (!existingUser) {
        console.log('Creating user profile for:', currentUser.id);
        
        const { error: insertError } = await supabase
          .from('users')
          .insert({
            id: currentUser.id,
            email: currentUser.email || '',
            full_name: currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'User',
            last_sign_in: new Date().toISOString()
          });
          
        if (insertError) {
          console.error('Error creating user profile:', insertError);
        } else {
          console.log('User profile created successfully');
        }
      } else {
        // Update the last_sign_in timestamp for existing users
        const { error: updateError } = await supabase
          .from('users')
          .update({
            last_sign_in: new Date().toISOString()
          })
          .eq('id', currentUser.id);

        if (updateError) {
          console.error('Error updating last_sign_in:', updateError);
        } else {
          console.log('Last sign in time updated successfully');
        }
      }
    } catch (err) {
      console.error('Error in createUserProfileIfNeeded:', err);
    }
  };

  // Function to broadcast auth state changes to other tabs
  const broadcastAuthState = (user: SupabaseUser | null) => {
    authChannel.postMessage({ type: 'AUTH_STATE_CHANGE', user });
  };

  useEffect(() => {
    let mounted = true;
    
    // Check for active session on component mount
    const fetchSession = async () => {
      try {
        // Ensure supabase client is available
        if (!supabase || !supabase.auth) {
          throw new Error('Supabase client not properly initialized');
        }

        console.log('Fetching initial session');
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        const currentUser = data.session?.user || null;
        console.log('Session fetched, user present:', !!currentUser);
        
        if (mounted) {
          // If we have a user, set authenticated immediately to prevent flashing
          if (currentUser) {
            setUser(currentUser);
            setSessionChecked(true);
            setLoading(false);
            broadcastAuthState(currentUser);
            
            // Create user profile in background
            createUserProfileIfNeeded(currentUser).catch(console.error);
          } else {
            setUser(null);
            setSessionChecked(true);
            setLoading(false);
            broadcastAuthState(null);
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
        if (mounted) {
          setError(error instanceof Error ? error.message : 'Unknown error');
          setSessionChecked(true);
          setLoading(false);
        }
      }
    };

    // Add a timeout to force loading to false after a reasonable time
    // This ensures we don't get stuck in loading state
    const timeoutId = window.setTimeout(() => {
      if (mounted && loading) {
        console.log('Forcing loading state to false after timeout');
        setLoading(false);
        setSessionChecked(true);
      }
    }, 3000);

    // Listen for auth state changes from other tabs
    const handleAuthMessage = (event: MessageEvent) => {
      if (event.data.type === 'AUTH_STATE_CHANGE') {
        setUser(event.data.user);
        setSessionChecked(true);
        setLoading(false);
      }
    };

    authChannel.addEventListener('message', handleAuthMessage);

    try {
      fetchSession();

      // Set up auth state change listener
      if (supabase && supabase.auth) {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (_event, currentSession) => {
            console.log('Auth state changed, event:', _event);
            const currentUser = currentSession?.user || null;
            console.log('Auth state user present:', !!currentUser);
            
            if (mounted) {
              setUser(currentUser);
              setSessionChecked(true);
              setLoading(false);
              broadcastAuthState(currentUser);
              
              // Reset user profile state when user changes
              const { reset } = useUserProfileStore.getState();
              reset();
              
              // Create user profile when auth state changes (sign in)
              if (currentUser) {
                createUserProfileIfNeeded(currentUser).catch(console.error);
              }
            }
          }
        );

        // Clean up subscription on unmount
        return () => {
          mounted = false;
          window.clearTimeout(timeoutId);
          subscription.unsubscribe();
          authChannel.removeEventListener('message', handleAuthMessage);
        };
      } else {
        // If supabase auth is not available, make sure to set loading to false
        console.log('Supabase auth not available, setting loading to false');
        setSessionChecked(true);
        setLoading(false);
      }
    } catch (err) {
      console.error('AuthProvider initialization error:', err);
      if (mounted) {
        setError('Failed to initialize authentication');
        setSessionChecked(true);
        setLoading(false);
      }
    }

    return () => {
      mounted = false;
      window.clearTimeout(timeoutId);
      authChannel.removeEventListener('message', handleAuthMessage);
    };
  }, [loading]);

  const signIn = async () => {
    try {
      setLoading(true);
      if (!supabase || !supabase.auth) {
        throw new Error('Supabase client not initialized');
      }
      console.log('Starting Azure OAuth sign-in');
      
      // Save the current URL path so we can redirect back after login
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/auth/callback') {
        console.log('Saving current path for redirect after login:', currentPath);
        sessionStorage.setItem('redirectTo', currentPath);
      }
      
      // Get the current hostname
      const hostname = window.location.hostname;
      console.log('Current hostname:', hostname);
      
      // Determine the appropriate redirect URL
      let redirectUrl;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Local development
        redirectUrl = `${window.location.origin}/auth/callback`;
      } else {
        // Production/staging environment - use the actual domain
        redirectUrl = `https://${hostname}/auth/callback`;
      }
      
      console.log('Using redirect URL:', redirectUrl);
      console.log('Window location:', {
        origin: window.location.origin,
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        href: window.location.href
      });
      console.log('Environment:', {
        NODE_ENV: import.meta.env.MODE,
        VITE_APP_URL: import.meta.env.VITE_APP_URL
      });
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email openid profile offline_access',
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        console.error('OAuth sign-in error:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error signing in:', error);
      setLoading(false);
      throw error;
    }
  };

  // For testing purposes only - in development
  const mockSignIn = () => {
    if (process.env.NODE_ENV !== 'development') return;
    
    // Create a mock user for testing
    const mockUser = {
      id: 'test-user-id',
      aud: 'authenticated',
      role: '',
      email: 'test@example.com',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      app_metadata: {},
      user_metadata: {
        full_name: 'Test User'
      },
      identities: [],
      confirmed_at: new Date().toISOString()
    } as unknown as SupabaseUser;
    
    setUser(mockUser);
    setLoading(false);
  };

  // For testing admin routes - in development
  const mockAdminSignIn = async () => {
    if (process.env.NODE_ENV !== 'development') return;
    
    try {
      setLoading(true);
      
      if (!supabase?.auth) {
        throw new Error('Supabase auth not initialized');
      }

      // Create a mock admin user for testing
      const mockUser = {
        id: 'admin-id',
        aud: 'authenticated',
        role: 'authenticated',
        email: 'admin@example.com',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        app_metadata: {
          provider: 'email',
          providers: ['email']
        },
        user_metadata: {
          full_name: 'Admin User',
          is_admin: true
        },
        identities: [],
        confirmed_at: new Date().toISOString()
      } as unknown as SupabaseUser;

      // First, try to clear any existing session
      await supabase.auth.signOut();
      localStorage.clear();
      
      // For local testing, bypass the supabase client entirely
      // Just directly set context state
      setUser(mockUser);
      
      // For testing purposes, set a flag in sessionStorage
      // that our frontend can check for mock authentication
      sessionStorage.setItem('mock_admin_auth', 'true');
      
      console.log('Mock admin sign in completed - context user and session set');

    } catch (error) {
      console.error('Error in mock admin sign in:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      if (!supabase || !supabase.auth) {
        throw new Error('Supabase client not initialized');
      }
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      // Clear user and session state
      setUser(null);
      // Reset user profile state
      const { reset } = useUserProfileStore.getState();
      reset();
      // Broadcast the sign out to other tabs
      broadcastAuthState(null);
      // Reset session check state
      setSessionChecked(true);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      signIn, 
      signOut, 
      isAuthenticated, 
      sessionChecked,
      mockSignIn, 
      mockAdminSignIn, 
      error 
    }}>
      {error && !loading ? (
        <div style={{ 
          padding: '20px', 
          margin: '20px auto', 
          maxWidth: '600px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          border: '1px solid #f5c6cb'
        }}>
          <h2>Authentication Error</h2>
          <p>{error}</p>
          <p>Please check your configuration and try again.</p>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}
